import { motion } from 'framer-motion';
import { MessageSquare } from 'lucide-react';
import type { EquipmentLine } from '../../types';
import { formatCurrency } from '../../utils';
import { ImageGallery } from '../common/ImageGallery';
import { ConditionBadge, QuantityBadge, FuelTypeBadge } from '../common/Badge';
import { EquipmentSpecs } from './EquipmentSpecs';
import { EquipmentOptions } from './EquipmentOptions';
import { useFeedback } from '../../context';
import { useProposalData } from '../../context';

interface EquipmentCardProps {
  equipment: EquipmentLine;
  index: number;
}

export function EquipmentCard({ equipment, index }: EquipmentCardProps) {
  const { openFeedbackModal, getFeedbackCount } = useFeedback();
  const proposal = useProposalData();
  const feedbackCount = getFeedbackCount('equipment', equipment.lineNumber);

  // Options are displayed for information only - unitPrice already includes them
  const optionsTotal = equipment.options.reduce((sum, opt) => sum + opt.price, 0);
  // Use extended if available (already calculated in NetSuite), otherwise unitPrice * quantity
  const lineTotal = equipment.extended || (equipment.unitPrice * equipment.quantity);

  // If more than 8 options, show them below the images in full-width layout
  const manyOptions = equipment.options.length > 8;

  return (
    <motion.div
      className="industrial-card group"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
    >
      {/* Industrial Corner Accent (from CSS) is applied via .industrial-card */}

      <div className="p-6 lg:p-8">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div className="flex-1 min-w-0">
            {/* Line Number Badge */}
            <div className="flex items-center gap-3 mb-2">
              <span className="inline-flex items-center justify-center w-8 h-8 bg-hul-navy text-white font-mono text-sm rounded">
                #{equipment.lineNumber}
              </span>
              <QuantityBadge quantity={equipment.quantity} />
              {equipment.condition && <ConditionBadge condition={equipment.condition} />}
            </div>

            {/* Title */}
            <h3 className="font-display text-2xl lg:text-3xl text-hul-navy tracking-wide">
              {equipment.manufacturer} {equipment.model}
            </h3>
            <p className="text-gray-600 mt-1">{equipment.description}</p>
          </div>

          {/* Price Block */}
          <div className="text-right">
            <div className="text-sm text-gray-500 mb-1">
              {equipment.quantity > 1 ? 'Extended Price' : 'Unit Price'}
            </div>
            <div className="font-mono text-3xl font-bold text-hul-red">
              {formatCurrency(lineTotal)}
            </div>
            {equipment.quantity > 1 && (
              <div className="text-sm text-gray-500 mt-1">
                {formatCurrency(equipment.unitPrice)} × {equipment.quantity}
              </div>
            )}
          </div>
        </div>

        {/* Content Grid */}
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left: Image Gallery */}
          <div>
            <ImageGallery
              images={equipment.images}
              alt={`${equipment.manufacturer} ${equipment.model}`}
              videoUrl={equipment.videoUrl}
            />
          </div>

          {/* Right: Specs & Services (options move below if many) */}
          <div className="space-y-6">
            {/* Fuel Type Badge */}
            <div className="flex gap-2">
              <FuelTypeBadge fuelType={equipment.fuelType} />
            </div>

            {/* Specifications */}
            <div>
              <h4 className="text-xs uppercase tracking-wider text-gray-500 mb-3 flex items-center gap-2">
                <span className="w-4 h-0.5 bg-hul-red" />
                Specifications
              </h4>
              <EquipmentSpecs equipment={equipment} />
            </div>

            {/* Options & Services - only show here if 8 or fewer options */}
            {!manyOptions && (
              <EquipmentOptions
                options={equipment.options}
                services={equipment.services}
              />
            )}
          </div>
        </div>

        {/* Full-width Options below images if more than 8 options */}
        {manyOptions && (
          <div className="mt-8">
            <EquipmentOptions
              options={equipment.options}
              services={equipment.services}
              fullWidth
            />
          </div>
        )}

        {/* Footer with Pricing Breakdown */}
        <div className="mt-8 pt-6 border-t border-gray-100">
          <div className="flex flex-wrap items-center justify-between gap-4">
            {/* Pricing Breakdown - options are already included in unitPrice */}
            <div className="flex flex-wrap gap-6 text-sm">
              <div>
                <span className="text-gray-500">Unit Price:</span>
                <span className="ml-2 font-mono text-hul-navy">
                  {formatCurrency(equipment.unitPrice)}
                </span>
              </div>
              {optionsTotal > 0 && (
                <div>
                  <span className="text-gray-500">Options Included:</span>
                  <span className="ml-2 font-mono text-gray-600">
                    {formatCurrency(optionsTotal)}
                  </span>
                </div>
              )}
              {equipment.quantity > 1 && (
                <div>
                  <span className="text-gray-500">Qty:</span>
                  <span className="ml-2 font-mono text-hul-navy">×{equipment.quantity}</span>
                </div>
              )}
            </div>

            {/* Feedback Button */}
            {proposal.feedbackEnabled && (
              <motion.button
                onClick={() => openFeedbackModal('equipment', equipment.lineNumber)}
                className="flex items-center gap-2 px-4 py-2 text-sm text-hul-navy hover:text-hul-red hover:bg-gray-50 rounded-lg transition-colors"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <MessageSquare className="w-4 h-4" />
                <span>Comment</span>
                {feedbackCount > 0 && (
                  <span className="w-5 h-5 bg-hul-orange text-white text-xs rounded-full flex items-center justify-center">
                    {feedbackCount}
                  </span>
                )}
              </motion.button>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}
